/**
 * EditDemandaModalV3 — Conceito C v3 (06/07/2026)
 * Refactor visual do card em dois painéis:
 *   esquerda = contexto fixo (status, prazo, solicitante, times, progresso, participantes)
 *   direita  = etapas navegáveis + responsável por etapa + checklist da fase
 * Campos de baixa frequência vivem na gaveta "Ver tudo / Editar card".
 *
 * Mesmíssima interface de props e payload de save do EditDemandaModal (v2) —
 * substituição drop-in. O arquivo antigo permanece no repo como fallback
 * (revert = trocar 1 linha de import em Producao/MinhasDemandas/TimeArea).
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import {
  X, Loader2, Trash2, ExternalLink, MessageSquare, Send, ThumbsUp,
  MessageSquareWarning, Sparkles, StickyNote, ChevronDown, ChevronUp, Pencil,
} from 'lucide-react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { displayStatus } from '@/lib/types';
import clsx from 'clsx';
import { CHECKLIST_TEMPLATES, SUB_ETAPAS_POR_TIPO, TIPOS_APOSENTADOS, TIPOS_FLUXO_BASICO, itensDaEtapa, parseProgresso, parseResponsaveis, checarAvanco } from '@/lib/checklists';
import type { TipoProduto, ChecklistItem, ResponsaveisMap, ProgressoMap } from '@/lib/checklists';
import type {
  Demanda, Solicitante, Pessoa, Area,
  Prioridade, Origem, StatusDemanda, SubEtapa,
  SubItemTipoB, Canal, Episodio,
} from '@/lib/types';
import { COORDENADORES_CANAL, resolveAreasTipoProduto } from '@/lib/materialAreaMap';
import { parseCreditos } from '@/lib/distribuicao';
import {
  Avatar, Badge, FacesStack, StagePill, ProgressSteps, InlineEditable,
} from '@/components/ds';
import type { Participante, BadgeVariant } from '@/components/ds';
import NotasPanel from './NotasPanel';
import { DistribuicaoSection } from './DistribuicaoSection';
import FileUpload from './FileUpload';

interface Props {
  demandaId: string | null;
  demandas: Demanda[];
  solicitantes: Solicitante[];
  pessoas: Pessoa[];
  areas: Area[];
  episodios?: Episodio[];
  onClose: () => void;
}

const PRIORIDADES: Prioridade[] = ['Crítica', 'Alta', 'Média', 'Baixa'];
const ORIGENS: Origem[] = ['Externo (contratual)', 'Apex (UN)', 'Research (interno)'];
const STATUSES: StatusDemanda[] = [
  'Triagem', 'Em produção', 'Em revisão', 'Aguardando aprovação',
  'Publicada', 'Arquivada', 'Cancelada',
];
const VERIF_ETAPAS: (typeof CHECKLIST_TEMPLATES)['Matéria de TV']['etapas'] = [
  { status: 'Triagem', itens: [] },
  { status: 'Em revisão', itens: [] },
  { status: 'Publicada', itens: [] },
];
const VERIF_LABEL: Partial<Record<StatusDemanda, string>> = {
  Triagem: 'A verificar', 'Em revisão': 'Revisão', Publicada: 'Concluído',
};
// Tipos de fluxo básico (TIPOS_FLUXO_BASICO) — relabel só visual (status canônico não muda).
const OUTRO_LABEL: Partial<Record<StatusDemanda, string>> = {
  Triagem: 'Briefing', 'Em revisão': 'Revisão', Publicada: 'Finalizada',
};
// Cobertura de evento (real time): Planejamento → Cobertura ao vivo → Concluída.
const COBERTURA_LABEL: Partial<Record<StatusDemanda, string>> = {
  Triagem: 'Planejamento', 'Em produção': 'Cobertura ao vivo', Publicada: 'Concluída',
};

/** Tipos de produto (legado) que são de conteúdo/vídeo e pedem o Canal (v2). */
const TIPOS_COM_CANAL: TipoProduto[] = [
  'Matéria de TV', 'Cliente Digital', 'Conteúdo editorial',
  'Vídeo institucional', 'Post de rede social', 'Campanha',
];

const STATUS_VARIANT: Record<StatusDemanda, BadgeVariant> = {
  'Triagem': 'blue',
  'Em produção': 'orange',
  'Em revisão': 'purple',
  'Aguardando aprovação': 'yellow',
  'Publicada': 'green',
  'Arquivada': 'gray',
  'Cancelada': 'red',
};

const PRIORIDADE_VARIANT: Record<Prioridade, BadgeVariant> = {
  'Crítica': 'red', 'Alta': 'purple', 'Média': 'blue', 'Baixa': 'gray',
};

/** Etapa → campo de responsáveis correspondente (resp*Ids da Demanda). */
type RespKey = 'briefing' | 'producao' | 'revisao' | 'aprovacao';
const RESP_KEY: Partial<Record<StatusDemanda, RespKey>> = {
  'Triagem': 'briefing',
  'Em produção': 'producao',
  'Em revisão': 'revisao',
  'Aguardando aprovação': 'aprovacao',
};
const RESP_LABEL: Record<RespKey, string> = {
  briefing: 'Briefing', producao: 'Produção', revisao: 'Revisão', aprovacao: 'Aprovação',
};

export function EditDemandaModalV3({
  demandaId, demandas, solicitantes, pessoas, areas, episodios = [], onClose,
}: Props) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const demanda = demandas.find((d) => d.id === demandaId);
  const isVerificacao = demanda?.tipoDemanda === 'verificacao_mac';
  const stLabel = (s: StatusDemanda) => {
    if (isVerificacao) return VERIF_LABEL[s] ?? displayStatus(s);
    if (tipoProduto === 'Cobertura de evento') return COBERTURA_LABEL[s] ?? displayStatus(s);
    if (TIPOS_FLUXO_BASICO.includes(tipoProduto)) return OUTRO_LABEL[s] ?? displayStatus(s);
    return displayStatus(s);
  };
  const temporadasQ = useQuery({ queryKey: ['temporadas'], queryFn: () => api.temporadas.list().then((r) => r.data) });
  const temporadas = temporadasQ.data ?? [];

  const [titulo, setTitulo] = useState('');
  const [solicitanteId, setSolicitanteId] = useState('');
  const [executorId, setExecutorId] = useState('');
  const [respEtapas, setRespEtapas] = useState<Record<RespKey, string[]>>({
    briefing: [], producao: [], revisao: [], aprovacao: [],
  });
  const [subEtapa, setSubEtapa] = useState<SubEtapa | ''>('');
  const [requerAprovacao, setRequerAprovacao] = useState(true);
  const [status, setStatus] = useState<StatusDemanda>('Triagem');
  const [prioridade, setPrioridade] = useState<Prioridade>('Média');
  const [origem, setOrigem] = useState<Origem>('Externo (contratual)');
  const [prazoSla, setPrazoSla] = useState('');
  const [dataPublicacao, setDataPublicacao] = useState('');
  const [pauta, setPauta] = useState('');
  const [briefing, setBriefing] = useState('');
  const [linkFinal, setLinkFinal] = useState('');
  const [tipoProduto, setTipoProduto] = useState<TipoProduto>('Outro');
  const [canal, setCanal] = useState<Canal | ''>('');
  const [areasIds, setAreasIds] = useState<string[]>([]);
  const [areasApoioIds, setAreasApoioIds] = useState<string[]>([]);
  const [colaboradores, setColaboradores] = useState<string[]>([]);
  const [episodioId, setEpisodioId] = useState('');
  const [temporadaId, setTemporadaId] = useState('');
  const [reelFatos, setReelFatos] = useState(false);
  const [checklistProgresso, setChecklistProgresso] = useState<ProgressoMap>({});
  const [checklistResponsaveis, setChecklistResponsaveis] = useState<ResponsaveisMap>({});
  const [itensCustom, setItensCustom] = useState<{id:string;titulo:string;etapa:string;concluido?:boolean;responsavelId?:string;prazo?:string;areaId?:string;texto?:string;estado?:string;natureza?:string;fase?:string;areaDona?:string;origem?:string}[]>([]);
  const [novoItemTitulo, setNovoItemTitulo] = useState('');
  const [novoItemEtapa, setNovoItemEtapa] = useState('Triagem');
  const [err, setErr] = useState<string | null>(null);

  // ---- v3: navegação de etapas + gaveta ----
  const [viewedEtapa, setViewedEtapa] = useState<StatusDemanda>('Triagem');
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Guarda o último demandaId: refetch do MESMO card (ex.: salvar crédito na
  // distribuição multi-cota) não pode recolher a edição — só troca de card reseta.
  const prevDemandaIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (demanda) {
      setTitulo(demanda.titulo);
      setSolicitanteId(demanda.solicitanteId);
      setExecutorId(demanda.executorId ?? '');
      setRespEtapas({
        briefing: demanda.respBriefingIds ?? [],
        producao: demanda.respProducaoIds ?? [],
        revisao: demanda.respRevisaoIds ?? [],
        aprovacao: demanda.respAprovacaoIds ?? [],
      });
      setSubEtapa(demanda.subEtapa ?? '');
      setRequerAprovacao(demanda.requerAprovacaoCliente ?? true);
      setStatus(demanda.status);
      setPrioridade(demanda.prioridade);
      setOrigem(demanda.origem);
      setPrazoSla(demanda.prazoSla ?? '');
      setDataPublicacao(demanda.dataPublicacao ?? '');
      setPauta(demanda.pauta ?? '');
      setBriefing(demanda.briefing ?? '');
      setLinkFinal(demanda.linkFinal ?? '');
      setTipoProduto((demanda.tipoProduto as TipoProduto) ?? 'Outro');
      setCanal(demanda.canal ?? '');
      setAreasIds(demanda.areasIds ?? []);
      setAreasApoioIds(demanda.areasApoioIds ?? []);
      setColaboradores(demanda.colaboradoresIds ?? []);
      setEpisodioId(demanda.episodioId ?? '');
      setTemporadaId(episodios.find((e) => e.id === demanda.episodioId)?.temporadaId ?? '');
      setReelFatos(demanda.reelFatos ?? false);
      setChecklistProgresso(parseProgresso(demanda.checklistProgresso));
      setChecklistResponsaveis(parseResponsaveis(demanda.checklistResponsaveis));
      if (demanda.customChecklist) {
        try { setItensCustom(JSON.parse(demanda.customChecklist)); } catch {}
      } else {
        setItensCustom([]);
      }
      setViewedEtapa(demanda.status);
      if (prevDemandaIdRef.current !== demandaId) setDrawerOpen(false);
      prevDemandaIdRef.current = demandaId;
      setErr(null);
    } else {
      prevDemandaIdRef.current = null;
    }
  }, [demandaId, demanda]);

  const save = useMutation({
    mutationFn: (body: Partial<Demanda>) =>
      api.demandas.update(demandaId!, body).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['demandas'] });
      onClose();
    },
    onError: (e) => {
      const msg = e instanceof ApiError ? (e.body as { message?: string })?.message ?? e.message : String(e);
      setErr(msg);
    },
  });

  const del = useMutation({
    mutationFn: () => api.demandas.delete(demandaId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['demandas'] });
      onClose();
    },
    onError: (e) => {
      const msg = e instanceof ApiError ? (e.body as { message?: string })?.message ?? e.message : String(e);
      setErr(msg);
    },
  });

  const [novoComentario, setNovoComentario] = useState('');
  const comentar = useMutation({
    mutationFn: (texto: string) => api.demandas.comentar(demandaId!, texto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['demandas'] });
      setNovoComentario('');
    },
    onError: (e) => {
      const msg = e instanceof ApiError ? (e.body as { message?: string })?.message ?? e.message : String(e);
      setErr(msg);
    },
  });

  // ======== derivações v3 (antes de qualquer return p/ manter ordem dos hooks) ========
  const pessoaById = useMemo(() => {
    const m = new Map<string, Pessoa>();
    pessoas.forEach((p) => m.set(p.id, p));
    return m;
  }, [pessoas]);

  const etapasTemplate = useMemo(
    () => {
      const base = isVerificacao ? VERIF_ETAPAS : (CHECKLIST_TEMPLATES[tipoProduto]?.etapas ?? []);
      // Card sem exigência de aprovação do cliente não passa por 'Aguardando aprovação' (fluxo básico: Briefing → Produção → Revisão → Finalizada).
      return requerAprovacao ? base : base.filter((e) => e.status !== 'Aguardando aprovação');
    },
    [tipoProduto, isVerificacao, requerAprovacao],
  );
  // Sub-etapas específicas do tipo (card inteligente) — vazio = fluxo básico, sem sub-etapa.
  const subGrupos = isVerificacao ? [] : (SUB_ETAPAS_POR_TIPO[tipoProduto] ?? []);
  const activeIdx = etapasTemplate.findIndex((e) => e.status === status);

  /**
   * Equipe da etapa — DERIVADA dos sub-checks (fonte de verdade da execução).
   * Cada item pode ter pessoas diferentes (ex.: captação = repórter + 2 cinegrafistas).
   */
  function equipeDaEtapa(st: StatusDemanda): string[] {
    const ids: string[] = [];
    for (const item of itensDaEtapa(tipoProduto, st)) {
      for (const id of checklistResponsaveis[item.id] ?? []) {
        if (!ids.includes(id)) ids.push(id);
      }
    }
    return ids;
  }
  /** Coordenador(es) da etapa — opcional (reusa os campos resp*Ids legados). */
  function coordDaEtapa(st: StatusDemanda): string[] {
    const key = RESP_KEY[st];
    return key ? respEtapas[key] : [];
  }
  /** Rosto(s) da etapa p/ pílulas: coordenador primeiro, senão equipe derivada. */
  function facesDaEtapa(st: StatusDemanda): string[] {
    const coord = coordDaEtapa(st);
    return coord.length > 0 ? coord : equipeDaEtapa(st);
  }

  // "em execução" = pessoas do PRÓXIMO ITEM ABERTO da etapa atual (o que está acontecendo agora)
  const progAtual = checklistProgresso[status] ?? {};
  const itemFeito = (i: ChecklistItem) => progAtual[i.id] === true || progAtual[i.titulo] === true;
  const proximoItem = itensDaEtapa(tipoProduto, status).find((i) => !itemFeito(i));
  const execIds = proximoItem ? (checklistResponsaveis[proximoItem.id] ?? []) : [];
  const execPessoas = (execIds.length > 0 ? execIds : facesDaEtapa(status))
    .map((id) => pessoaById.get(id))
    .filter(Boolean) as Pessoa[];
  const execEtapaLabel = subEtapa || displayStatus(status);

  const participantes = useMemo<Participante[]>(() => {
    const list: Participante[] = [];
    const push = (id: string, papel?: string, atual?: boolean) => {
      const p = pessoaById.get(id);
      if (!p) return;
      const ex = list.find((x) => x.id === id);
      if (ex) { if (atual) ex.atual = true; return; }
      list.push({ id, nome: p.nome, avatarUrl: p.avatarUrl, papel, atual });
    };
    // pessoas dos sub-checks (fonte de verdade) — papel = etapa do item
    etapasTemplate.forEach((et) => {
      et.itens.forEach((item) => {
        (checklistResponsaveis[item.id] ?? []).forEach((id) =>
          push(id, displayStatus(et.status), et.status === status));
      });
    });
    // coordenadores de etapa (opcional)
    (Object.keys(RESP_KEY) as StatusDemanda[]).forEach((st) => {
      const key = RESP_KEY[st]!;
      respEtapas[key].forEach((id) => push(id, `Coord. ${RESP_LABEL[key]}`, st === status));
    });
    if (executorId) push(executorId, 'Executor');
    colaboradores.forEach((id) => push(id, 'Colaborador'));
    return list;
  }, [pessoaById, etapasTemplate, respEtapas, checklistResponsaveis, executorId, colaboradores, status]);

  const creditos = parseCreditos(demanda?.distribuicaoCreditos);
  const creditosAguardando = creditos.filter((c) => c.status === 'aguardando').length;

  if (!demandaId || !demanda) return null;

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!titulo.trim()) { setErr('Título é obrigatório'); return; }
    setErr(null);
    save.mutate({
      titulo: titulo.trim(),
      solicitanteId,
      executorId: executorId || undefined,
      status,
      prioridade,
      origem,
      prazoSla: prazoSla || undefined,
      dataPublicacao: dataPublicacao || undefined,
      pauta: pauta.trim() || undefined,
      briefing: briefing.trim() || undefined,
      linkFinal: linkFinal.trim() || undefined,
      respBriefingIds: respEtapas.briefing,
      respProducaoIds: respEtapas.producao,
      respRevisaoIds: respEtapas.revisao,
      respAprovacaoIds: respEtapas.aprovacao,
      subEtapa: (subEtapa || undefined) as SubEtapa | undefined,
      requerAprovacaoCliente: requerAprovacao,
      tipoProduto,
      canal: (canal || undefined) as Canal | undefined,
      areasIds,
      areasApoioIds,
      colaboradoresIds: colaboradores,
      episodioId: episodioId || undefined,
      reelFatos,
      checklistProgresso: JSON.stringify(checklistProgresso),
      checklistResponsaveis: JSON.stringify(checklistResponsaveis),
      customChecklist: JSON.stringify(itensCustom),
    });
  }

  function handleDelete() {
    if (confirm(`Apagar a demanda "${demanda?.titulo ?? ""}"? Essa ação não pode ser desfeita.`)) {
      del.mutate();
    }
  }

  const canEdit = user?.papel === 'Coordenação' || user?.papel === 'Executor';
  const canDelete = user?.papel === 'Coordenação';

  function setOwnersDaEtapa(st: StatusDemanda, ids: string[]) {
    const key = RESP_KEY[st];
    if (!key) return;
    setRespEtapas((r) => ({ ...r, [key]: ids }));
  }

  const areaNome = (id: string) => areas.find((a) => a.id === id)?.nome ?? id;
  const donoNomes = areasIds.map(areaNome).join(' + ');
  const apoioNomes = areasApoioIds.map(areaNome).join(' + ');
  const solicitanteNome = solicitantes.find((s) => s.id === solicitanteId)?.nome ?? demanda.solicitanteNome ?? '—';

  const fmtData = (iso: string) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return y && m && d ? `${d}/${m}/${y}` : iso;
  };

  const avanco = checarAvanco(tipoProduto, status, checklistProgresso);

  // 08/jul/2026 (decisão Renan) — cards de evento usam checklist CUSTOM, que o
  // auto-avanço do template ignora. Quando todos os itens custom estiverem
  // concluídos, oferecemos um CTA explícito pra mover o card à próxima etapa
  // (em vez de exigir que a pessoa descubra o dropdown de Status).
  const statusFinal = status === 'Publicada' || status === 'Arquivada' || status === 'Cancelada';
  const customCompleto = itensCustom.length > 0 &&
    itensCustom.every((i) => i.estado === 'concluido' || i.concluido);
  const proximaEtapaCustom: StatusDemanda | null = (() => {
    if (!customCompleto || statusFinal || avanco.proximoStatus) return null;
    const ordem: StatusDemanda[] = etapasTemplate.length > 0
      ? etapasTemplate.map((e) => e.status)
      : ['Triagem', 'Em produção', 'Em revisão', 'Publicada'];
    const idx = ordem.indexOf(status);
    return idx >= 0 && idx < ordem.length - 1 ? ordem[idx + 1] : null;
  })();

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/60 backdrop-blur-sm"
      tabIndex={-1} ref={(el)=>{if(el&&el.dataset.esc!=='1'){el.dataset.esc='1';el.focus();}}} onKeyDown={(e)=>{if(e.key==='Escape')onClose();}}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-panel border-l border-border w-full max-w-4xl h-full overflow-y-auto flex flex-col"
      >
        {/* ===== header (compacto — id do registro fica só no tooltip) ===== */}
        <div className="flex items-center justify-between px-5 py-2.5 border-b border-border sticky top-0 bg-panel z-10">
          <h2 className="text-sm font-bold" title={demanda.id}>Detalhes da demanda</h2>
          <button type="button" onClick={onClose} className="text-muted hover:text-text">
            <X className="w-[18px] h-[18px]" />
          </button>
        </div>

        <div className="flex-1">
          <div className="grid md:grid-cols-[300px_1fr]">
            {/* ============ PAINEL ESQUERDO · CONTEXTO ============ */}
            <div className="bg-panel-2/60 md:border-r border-b md:border-b-0 border-border p-5">
              <div className="flex gap-1.5 flex-wrap">
                <Badge variant={STATUS_VARIANT[status]}>{displayStatus(status)}</Badge>
                <Badge variant={PRIORIDADE_VARIANT[prioridade]}>{prioridade}</Badge>
              </div>

              <h3 className="text-[15px] font-extrabold leading-snug mt-2 text-text">{titulo}</h3>
              <div className="text-[11.5px] text-muted mt-0.5">
                {tipoProduto}
                {donoNomes && <> · {donoNomes} <span className="text-text-2">(dono)</span></>}
                {apoioNomes && <> + {apoioNomes}</>}
              </div>

              {/* selo em execução — equipe do próximo item aberto (pode ser mais de 1 pessoa) */}
              <div className="flex items-center gap-3 rounded-xl border border-blue/30 bg-blue/5 px-3 py-2.5 mt-4">
                {execPessoas.length > 0 ? (
                  <FacesStack
                    size="md"
                    max={3}
                    pessoas={execPessoas.map((p, i) => ({
                      id: p.id, nome: p.nome, avatarUrl: p.avatarUrl,
                      papel: proximoItem?.titulo ?? execEtapaLabel, atual: i === 0,
                    }))}
                  />
                ) : (
                  <span className="w-9 h-9 shrink-0 rounded-full border-2 border-yellow/50 bg-yellow/10 text-yellow flex items-center justify-center font-extrabold">?</span>
                )}
                <div className="leading-tight min-w-0">
                  <div className="text-[11px] font-extrabold uppercase tracking-wide text-blue">em execução</div>
                  <div className="text-sm font-bold text-text truncate">{execEtapaLabel}</div>
                  {proximoItem && (
                    <div className="text-[10px] text-muted truncate">{proximoItem.titulo}</div>
                  )}
                </div>
              </div>

              {/* participantes */}
              {participantes.length > 0 && (
                <>
                  <div className="text-[10px] text-muted uppercase tracking-widest mt-4 mb-1.5">
                    Participantes · quem já tocou o card
                  </div>
                  <FacesStack pessoas={participantes} max={6} />
                </>
              )}

              {/* progresso */}
              {etapasTemplate.length > 0 && (
                <>
                  <div className="text-[10px] text-muted uppercase tracking-widest mt-4 mb-1.5">
                    {activeIdx >= 0
                      ? `Progresso · etapa ${activeIdx + 1} de ${etapasTemplate.length}`
                      : `Progresso · ${stLabel(status)}`}
                  </div>
                  <ProgressSteps
                    total={etapasTemplate.length}
                    done={activeIdx >= 0 ? activeIdx : status === 'Publicada' ? etapasTemplate.length : 0}
                    now={activeIdx >= 0 ? activeIdx : undefined}
                  />
                </>
              )}

              {/* meta editável inline */}
              <div className="mt-4 space-y-3">
                <div>
                  <div className="text-[10px] text-muted uppercase tracking-widest mb-0.5">Status</div>
                  <InlineEditable
                    value={status}
                    options={(isVerificacao ? (['Triagem', 'Em revisão', 'Publicada'] as StatusDemanda[]) : STATUSES).map((s) => ({ value: s, label: stLabel(s) }))}
                    onChange={(v) => { setStatus(v as StatusDemanda); setViewedEtapa(v as StatusDemanda); }}
                    disabled={!canEdit}
                  />
                </div>
                <div>
                  <div className="text-[10px] text-muted uppercase tracking-widest mb-0.5">Prioridade</div>
                  <InlineEditable
                    value={prioridade}
                    options={PRIORIDADES.map((p) => ({ value: p, label: p }))}
                    onChange={(v) => setPrioridade(v as Prioridade)}
                    disabled={!canEdit}
                  />
                </div>
                <div>
                  <div className="text-[10px] text-muted uppercase tracking-widest mb-0.5">Solicitante</div>
                  <InlineEditable
                    value={solicitanteId}
                    display={solicitanteNome}
                    options={solicitantes.map((s) => ({ value: s.id, label: s.nome }))}
                    onChange={setSolicitanteId}
                    disabled={!canEdit}
                  />
                </div>
                <div>
                  <div className="text-[10px] text-muted uppercase tracking-widest mb-0.5">Prazo</div>
                  <InlineDate value={prazoSla} onChange={setPrazoSla} disabled={!canEdit} display={fmtData(prazoSla) || '— sem prazo —'} pending={!prazoSla} />
                </div>
                {(creditos.length > 0 || demanda.contratosIds?.length) && (
                  <div>
                    <div className="text-[10px] text-muted uppercase tracking-widest mb-0.5">Distribuição</div>
                    <button
                      type="button"
                      onClick={() => setDrawerOpen(true)}
                      className={clsx(
                        'inline-flex items-center gap-1.5 rounded-md border border-transparent px-2 py-1 -ml-2 text-sm font-semibold',
                        'hover:border-border hover:bg-panel-3',
                        creditosAguardando > 0 ? 'text-yellow' : 'text-text',
                      )}
                    >
                      {creditosAguardando > 0 && <span className="w-2 h-2 rounded-full bg-yellow" />}
                      {creditos.length > 0
                        ? `${creditos.length} contrato${creditos.length > 1 ? 's' : ''} · ${creditosAguardando > 0 ? `${creditosAguardando} aguardando` : 'confirmado'}`
                        : `${demanda.contratosIds?.length ?? 0} contrato(s) vinculados`}
                      <Pencil className="w-3 h-3 text-muted" />
                    </button>
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={() => setDrawerOpen((v) => !v)}
                className="w-full mt-5 border border-border text-text-2 hover:border-blue hover:text-text rounded-lg py-2 text-xs font-bold transition-colors inline-flex items-center justify-center gap-1.5"
              >
                {drawerOpen ? <>Fechar edição completa <ChevronUp className="w-3.5 h-3.5" /></> : <>Ver tudo / Editar card <ChevronDown className="w-3.5 h-3.5" /></>}
              </button>
            </div>

            {/* ============ PAINEL DIREITO · ETAPA ATUAL ============ */}
            <div className="p-5">
              {etapasTemplate.length > 0 ? (
                <>
                  <div className="text-[10px] text-muted uppercase tracking-widest font-bold mb-2">
                    Etapas · clique para navegar
                  </div>
                  <div className="flex gap-1.5 flex-wrap mb-4">
                    {etapasTemplate.map((et, i) => {
                      const ownerId = facesDaEtapa(et.status)[0];
                      const owner = ownerId ? pessoaById.get(ownerId) : undefined;
                      return (
                        <StagePill
                          key={et.status}
                          label={stLabel(et.status)}
                          state={activeIdx >= 0 && i < activeIdx ? 'done' : i === activeIdx ? 'now' : 'todo'}
                          onClick={() => setViewedEtapa(et.status)}
                          className={clsx(viewedEtapa === et.status && 'ring-2 ring-blue/30 !border-blue')}
                          trailing={owner ? <Avatar nome={owner.nome} avatarUrl={owner.avatarUrl} size="sm" tooltip={false} ring={i === activeIdx} /> : undefined}
                        />
                      );
                    })}
                  </div>

                  {itensCustom.length > 0 && (
                    <div className="mt-4 rounded-lg border border-blue/40 bg-blue/5 p-3">
                      <div className="text-[10px] text-blue uppercase tracking-widest font-bold mb-1">
                        {isVerificacao ? 'O que verificar' : 'Checklist de verificação'} · {itensCustom.filter((i) => i.estado === 'concluido' || i.concluido).length}/{itensCustom.length}
                      </div>
                      {isVerificacao && (
                        <div className="text-[11px] text-muted mb-2">Confira/atualize cada ativação (texto, arte e tela) e marque ao concluir.</div>
                      )}
                      <div className="space-y-1.5">
                        {itensCustom.map((it) => {
                          const done = it.estado === 'concluido' || !!it.concluido;
                          return (
                            <label key={it.id} className={clsx('flex items-start gap-2 rounded-md px-2 py-1.5 text-xs transition-colors', done ? 'bg-panel-3 text-muted' : 'bg-panel-2', canEdit ? 'cursor-pointer' : 'cursor-default')}>
                              <input
                                type="checkbox"
                                checked={done}
                                disabled={!canEdit}
                                onChange={() => {
                                  const next = itensCustom.map((x) => (x.id === it.id ? (x.texto !== undefined ? { ...x, estado: done ? 'pendente' : 'concluido' } : { ...x, concluido: !done }) : x));
                                  setItensCustom(next);
                                  if (canEdit) save.mutate({ customChecklist: JSON.stringify(next) });
                                }}
                                className="mt-0.5 shrink-0"
                              />
                              <span className={clsx('flex-1 min-w-0', done && 'line-through')}>
                                {it.texto ?? it.titulo}
                                {!isVerificacao && it.etapa && <span className="ml-1.5 text-[10px] text-muted">· {it.etapa}</span>}
                              </span>
                            </label>
                          );
                        })}
                      </div>

                      {proximaEtapaCustom && canEdit && (
                        <div className="mt-2.5 bg-green/10 border border-green/30 rounded-lg p-2.5 flex items-center gap-2">
                          <Sparkles className="w-3.5 h-3.5 text-green shrink-0" />
                          <span className="text-[11px] text-green flex-1">
                            Checklist completo! Este card não avança sozinho.
                          </span>
                          <button
                            type="button"
                            disabled={save.isPending}
                            onClick={() => save.mutate({ status: proximaEtapaCustom, customChecklist: JSON.stringify(itensCustom) })}
                            className="btn-primary !py-1 !px-2.5 text-[11px] disabled:opacity-50"
                          >
                            Mover para {stLabel(proximaEtapaCustom)} →
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  {!isVerificacao && (<>
                  <StageBox
                    etapa={viewedEtapa}
                    isAtual={viewedEtapa === status}
                    tipo={tipoProduto}
                    pessoas={pessoas}
                    equipe={equipeDaEtapa(viewedEtapa)}
                    coord={coordDaEtapa(viewedEtapa)}
                    onCoordChange={(ids) => setOwnersDaEtapa(viewedEtapa, ids)}
                    temRespDireto={!!RESP_KEY[viewedEtapa]}
                    progresso={checklistProgresso}
                    onProgressoChange={setChecklistProgresso}
                    responsaveis={checklistResponsaveis}
                    onResponsaveisChange={setChecklistResponsaveis}
                    disabled={!canEdit}
                  />

                  {viewedEtapa === status && avanco.proximoStatus && (
                    <div className="mt-3 bg-blue/10 border border-blue/30 rounded-lg p-2.5 text-[11px] text-blue-light flex items-start gap-2">
                      <Sparkles className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                      <div>
                        Etapa completa! Ao salvar, o card vai pular automaticamente para <strong>{displayStatus(avanco.proximoStatus)}</strong>.
                      </div>
                    </div>
                  )}
                  </>)}
                </>
              ) : (
                <div className="bg-panel-2 border border-border rounded-lg p-4 text-xs text-muted">
                  Este tipo de produto não tem etapas com checklist. Use "Ver tudo / Editar card" para os campos.
                </div>
              )}
            </div>
          </div>

          {/* ============ GAVETA · VER TUDO / EDITAR ============ */}
          {drawerOpen && (
            <div className="border-t border-dashed border-border p-6 space-y-4">
              <div className="text-[10px] text-muted uppercase tracking-widest font-bold">
                Editar card completo
              </div>

              <Field label="Título" required>
                <input
                  className="input w-full"
                  value={titulo}
                  onChange={(e) => setTitulo(e.target.value)}
                  disabled={!canEdit}
                />
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Origem">
                  <select
                    className="input w-full"
                    value={origem}
                    onChange={(e) => setOrigem(e.target.value as Origem)}
                    disabled={!canEdit}
                  >
                    {ORIGENS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </Field>
                <Field label="Data publicação">
                  <input
                    type="date"
                    className="input w-full"
                    value={dataPublicacao}
                    onChange={(e) => setDataPublicacao(e.target.value)}
                    disabled={!canEdit}
                  />
                </Field>
              </div>

              <Field label="Executor designado (legado — prefira responsável por etapa)">
                <select
                  className="input w-full"
                  value={executorId}
                  onChange={(e) => setExecutorId(e.target.value)}
                  disabled={!canEdit}
                >
                  <option value="">— sem executor designado —</option>
                  {pessoas
                    .filter((p) => p.ativo)
                    .map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
                </select>
              </Field>

              <Field label="Pauta / Tema">
                <input
                  className="input w-full"
                  value={pauta}
                  onChange={(e) => setPauta(e.target.value)}
                  disabled={!canEdit}
                />
              </Field>

              <Field label="Briefing">
                <textarea
                  rows={4}
                  className="input w-full resize-none"
                  value={briefing}
                  onChange={(e) => setBriefing(e.target.value)}
                  disabled={!canEdit}
                />
              </Field>

              <Field label="Link da peça final">
                <div className="flex gap-2">
                  <input
                    className="input w-full"
                    placeholder="https://..."
                    value={linkFinal}
                    onChange={(e) => setLinkFinal(e.target.value)}
                    disabled={!canEdit}
                  />
                  {linkFinal && (
                    <a
                      href={linkFinal} target="_blank" rel="noreferrer"
                      className="btn-secondary !px-3"
                      title="Abrir"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </div>
              </Field>

              {demandaId && (
                <Field label="Arquivos anexados">
                  <FileUpload demandaId={demandaId} readOnly={!canEdit} />
                </Field>
              )}

              {/* === S1: Distribuição multi-cota === */}
              <DistribuicaoSection demanda={demanda} solicitantes={solicitantes} />

              {/* === Tipo B: Sub-items (clientes dentro do card) === */}
              {demanda.categoriaDemanda === 'tipo_b' && demanda.subItemsTipoB && (
                <SubItemsTipoBSection
                  subItemsJson={demanda.subItemsTipoB}
                  canEdit={canEdit}
                  onUpdate={(updated) => {
                    save.mutate({ subItemsTipoB: JSON.stringify(updated) });
                  }}
                />
              )}

              {/* === Custom checklist inter-times === */}
              {canEdit && !isVerificacao && (
                <CustomChecklistSection
                  itens={itensCustom}
                  onChange={setItensCustom}
                  pessoas={pessoas}
                  areas={areas}
                  novoTitulo={novoItemTitulo}
                  setNovoTitulo={setNovoItemTitulo}
                  novoEtapa={novoItemEtapa}
                  setNovoEtapa={setNovoItemEtapa}
                />
              )}

              {/* Métricas de engajamento */}
              {demanda?.metricas && (demanda.metricas.views != null || demanda.metricas.engajamento != null) && (
                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border border-blue/20 rounded-lg p-3">
                  <div className="text-[10px] font-bold text-text-2 uppercase tracking-wider mb-2">Métricas de engajamento</div>
                  <div className="grid grid-cols-3 gap-3">
                    {demanda.metricas.views != null && (
                      <div>
                        <div className="text-lg font-extrabold text-text">{demanda.metricas.views.toLocaleString('pt-BR')}</div>
                        <div className="text-[10px] text-muted">views</div>
                      </div>
                    )}
                    {demanda.metricas.engajamento != null && (
                      <div>
                        <div className="text-lg font-extrabold text-text">{demanda.metricas.engajamento}%</div>
                        <div className="text-[10px] text-muted">engajamento</div>
                      </div>
                    )}
                    {demanda.metricas.ultimaSync && (
                      <div>
                        <div className="text-xs font-semibold text-text">{new Date(demanda.metricas.ultimaSync).toLocaleDateString('pt-BR')}</div>
                        <div className="text-[10px] text-muted">última sync</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <Field label="Tipo de produto · define o checklist da etapa">
                <select
                  className="input w-full text-sm"
                  value={tipoProduto}
                  onChange={(e) => setTipoProduto(e.target.value as TipoProduto)}
                  disabled={!canEdit}
                >
                  {(Object.keys(CHECKLIST_TEMPLATES) as TipoProduto[]).filter((t) => !TIPOS_APOSENTADOS.includes(t) || t === tipoProduto).map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                <p className="text-[10px] text-muted mt-1">
                  Cada tipo tem um fluxo próprio. Quando todos os itens da etapa atual são ticados, o card pula automaticamente pra próxima coluna.
                </p>
              </Field>

              {TIPOS_COM_CANAL.includes(tipoProduto) && (
                <Field label="Canal · aciona os coordenadores de Conteúdo">
                  <select
                    className="input w-full text-sm"
                    value={canal}
                    onChange={(e) => setCanal(e.target.value as Canal | '')}
                    disabled={!canEdit}
                  >
                    <option value="">— definir canal —</option>
                    <option value="digital">Digital</option>
                    <option value="tv">TV</option>
                  </select>
                  {canal && (
                    <p className="text-[10px] text-muted mt-1">
                      Aciona: {COORDENADORES_CANAL[canal].join(' · ')}.
                    </p>
                  )}
                  {!canal && (
                    <p className="text-[10px] text-muted mt-1">
                      Digital → Valentina/Agnes · TV → Fernanda Batista.
                    </p>
                  )}
                </Field>
              )}

              {tipoProduto === 'Matéria de TV' && (
                <Field label="Programa · Temporada · Episódio">
                  <div className="grid grid-cols-2 gap-3">
                    <select
                      className="input w-full text-sm"
                      value={temporadaId}
                      onChange={(e) => { setTemporadaId(e.target.value); setEpisodioId(''); }}
                      disabled={!canEdit}
                    >
                      <option value="">— temporada —</option>
                      {temporadas
                        .slice()
                        .sort((a, b) => (b.numero ?? 0) - (a.numero ?? 0))
                        .map((t) => (
                          <option key={t.id} value={t.id}>{t.temporada || `${t.programa ?? ''} · ${t.numero ?? ''}`}</option>
                        ))}
                    </select>
                    <select
                      className="input w-full text-sm"
                      value={episodioId}
                      onChange={(e) => setEpisodioId(e.target.value)}
                      disabled={!canEdit || !temporadaId}
                    >
                      <option value="">— episódio —</option>
                      {episodios
                        .filter((ep) => ep.titulo && (temporadaId ? ep.temporadaId === temporadaId : true))
                        .slice()
                        .sort((a, b) => Number(a.numero ?? 0) - Number(b.numero ?? 0))
                        .map((ep) => (
                          <option key={ep.id} value={ep.id}>{ep.titulo}</option>
                        ))}
                    </select>
                  </div>
                  <p className="text-[10px] text-muted mt-1">
                    Escolha a temporada (playlist) e o episódio. Temporadas são criadas em Entregas → Programa TV/YouTube.
                  </p>
                  <label className="flex items-center gap-2 text-text-2 text-xs mt-2">
                    <input
                      type="checkbox"
                      checked={reelFatos}
                      onChange={(e) => setReelFatos(e.target.checked)}
                      disabled={!canEdit}
                      className="w-3.5 h-3.5 accent-blue-light"
                    />
                    Reel Fatos — este card de Fatos Relevantes deve gerar um reel p/ o Instagram
                  </label>
                </Field>
              )}

              <ComentariosSection
                comentarios={demanda.comentarios}
                onAdd={(t) => comentar.mutate(t)}
                sending={comentar.isPending}
                novo={novoComentario}
                setNovo={setNovoComentario}
                canPost={canEdit}
              />

              <Field label={<span className="flex items-center gap-1.5"><StickyNote className="w-3.5 h-3.5" /> Notas internas</span>}>
                <NotasPanel recursoTipo="demanda" recursoId={demanda.id} />
              </Field>

              <Field label="Colaboradores">
                <PessoasMultiPicker
                  pessoas={pessoas}
                  selecionados={colaboradores}
                  onChange={setColaboradores}
                  disabled={!canEdit}
                  placeholder="+ adicionar colaborador"
                />
                <p className="text-[10px] text-muted mt-1.5">
                  Pessoas que participam deste card avulso (ad-hoc) — ex.: quem grava/edita. Cards gerados por contrato herdam os times pela matriz.
                </p>
              </Field>

              <Field label="Sub-etapa atual">
                {subGrupos.length > 0 ? (
                  <>
                    <select
                      className="input w-full text-sm"
                      value={subEtapa}
                      onChange={(e) => setSubEtapa(e.target.value as SubEtapa | '')}
                      disabled={!canEdit}
                    >
                      <option value="">— sem sub-etapa —</option>
                      {subGrupos.map((g) => (
                        <optgroup key={g.grupo} label={g.grupo}>
                          {g.opcoes.map((o) => (
                            <option key={o} value={o}>{o}</option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    <p className="text-[10px] text-muted mt-1">Detalha o momento dentro do status — aparece no selo "em execução". Ex.: matéria TV em Produção → Captação ou Roteiro.</p>
                  </>
                ) : (
                  <>
                    {subEtapa && (
                      <select
                        className="input w-full text-sm"
                        value={subEtapa}
                        onChange={(e) => setSubEtapa(e.target.value as SubEtapa | '')}
                        disabled={!canEdit}
                      >
                        <option value="">— sem sub-etapa —</option>
                        <option value={subEtapa}>{subEtapa}</option>
                      </select>
                    )}
                    <p className="text-[10px] text-muted mt-1">Este tipo segue o fluxo básico do card (etapas acima), sem sub-etapas específicas — cada tipo ganhará seu fluxo próprio conforme for definido com os times.</p>
                  </>
                )}
              </Field>

              <Field label="Aprovação do cliente">
                <label className="flex items-center gap-2 text-text-2 text-xs">
                  <input
                    type="checkbox"
                    checked={requerAprovacao}
                    onChange={(e) => setRequerAprovacao(e.target.checked)}
                    disabled={!canEdit}
                    className="w-3.5 h-3.5 accent-pink"
                  />
                  Esta peça precisa de aprovação do cliente antes de finalizar
                </label>
                <p className="text-[10px] text-muted mt-1">
                  Desmarque pra peças que não passam pelo cliente (matéria de TV, conteúdo interno). Mover pra "Aguardando aprovação" com esta opção desmarcada vai direto pra "Finalizado".
                </p>
              </Field>

              <Field label="Áreas (dono + apoio)">
                {canEdit && (
                  <div className="flex items-center gap-2 mb-2">
                    <button
                      type="button"
                      onClick={() => {
                        const { areasIds: dono, areasApoioIds: apoio } = resolveAreasTipoProduto(tipoProduto, undefined, canal || undefined);
                        if (dono.length === 0 && apoio.length === 0) return;
                        setAreasIds(dono);
                        setAreasApoioIds(apoio.filter((id) => !dono.includes(id)));
                      }}
                      className="text-[11px] px-2 py-1 rounded border border-blue/40 text-blue-light hover:bg-blue/10"
                    >
                      Sugerir pelo tipo (dono + apoio)
                    </button>
                    <select
                      className="input !py-1 !px-2 text-[11px]"
                      value=""
                      onChange={(e) => {
                        const id = e.target.value;
                        if (id && !areasIds.includes(id)) {
                          setAreasIds((arr) => [...arr, id]);
                          setAreasApoioIds((arr) => arr.filter((x) => x !== id));
                        }
                      }}
                    >
                      <option value="">+ adicionar área dona…</option>
                      {areas.filter((a) => !areasIds.includes(a.id)).map((a) => (
                        <option key={a.id} value={a.id}>{a.nome}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="text-[10px] font-bold uppercase tracking-wider text-muted mb-1">Donas</div>
                <div className="flex flex-wrap gap-1.5">
                  {areasIds.length === 0 && <span className="text-[11px] text-muted">— nenhuma —</span>}
                  {areasIds.map((id) => (
                    <span key={id} className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-blue/10 text-blue-light border border-blue/20">
                      {areaNome(id)}
                      {canEdit && (
                        <button type="button" onClick={() => setAreasIds((arr) => arr.filter((x) => x !== id))}
                          className="hover:text-red" title="Remover"><X className="w-3 h-3" /></button>
                      )}
                    </span>
                  ))}
                </div>
                {(areasApoioIds.length > 0 || canEdit) && (
                  <>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-muted mt-2 mb-1">Apoio</div>
                    <div className="flex flex-wrap gap-1.5">
                      {areasApoioIds.length === 0 && <span className="text-[11px] text-muted">— nenhum —</span>}
                      {areasApoioIds.map((id) => (
                        <span key={id} className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-panel-2 text-muted border border-border">
                          {areaNome(id)}
                          {canEdit && (
                            <button type="button" onClick={() => setAreasApoioIds((arr) => arr.filter((x) => x !== id))}
                              className="hover:text-red" title="Remover"><X className="w-3 h-3" /></button>
                          )}
                        </span>
                      ))}
                    </div>
                  </>
                )}
                <p className="text-[11px] text-muted mt-1">
                  Roteamento por material (dono vs apoio) — matriz v2. "Sugerir pelo tipo" preenche pela matriz; edite conforme necessário.
                </p>
              </Field>
            </div>
          )}

          {err && (
            <div className="mx-6 mb-4 text-sm text-red bg-red/10 border border-red/30 rounded-lg px-3 py-2">
              {err}
            </div>
          )}
        </div>

        {/* ===== footer (compacto) ===== */}
        <div className="px-5 py-2 border-t border-border flex items-center justify-between gap-3 sticky bottom-0 bg-panel">
          {canDelete ? (
            <button
              type="button" onClick={handleDelete}
              disabled={del.isPending}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-red hover:bg-red/10 rounded-lg transition-colors text-sm"
            >
              <Trash2 className="w-4 h-4" />
              Apagar
            </button>
          ) : <div />}
          <div className="flex items-center gap-3">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancelar
            </button>
            {canEdit && (
              <button type="submit" disabled={save.isPending} className="btn-primary disabled:opacity-50">
                {save.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                Salvar
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}

/* =========================================================
   STAGE BOX — responsável da etapa + checklist da fase
   ========================================================= */
function StageBox({
  etapa, isAtual, tipo, pessoas, equipe, coord, onCoordChange, temRespDireto,
  progresso, onProgressoChange, responsaveis, onResponsaveisChange, disabled,
}: {
  etapa: StatusDemanda;
  isAtual: boolean;
  tipo: TipoProduto;
  pessoas: Pessoa[];
  /** equipe derivada dos sub-checks (read-only — atribuição acontece nos itens). */
  equipe: string[];
  /** coordenador(es) da etapa — opcional (campos resp*Ids legados). */
  coord: string[];
  onCoordChange: (ids: string[]) => void;
  /** etapa tem campo resp*Ids próprio (Briefing/Produção/Revisão/Aprovação)? */
  temRespDireto: boolean;
  progresso: ProgressoMap;
  onProgressoChange: (p: ProgressoMap) => void;
  responsaveis: ResponsaveisMap;
  onResponsaveisChange: (r: ResponsaveisMap) => void;
  disabled?: boolean;
}) {
  const itens = itensDaEtapa(tipo, etapa);
  const etapaProg = progresso[etapa] ?? {};
  // Compat com formato antigo: marcação podia ser por título OU por id
  const isDone = (i: ChecklistItem) => etapaProg[i.id] === true || etapaProg[i.titulo] === true;
  const feitos = itens.filter(isDone).length;
  const byId = (id: string) => pessoas.find((p) => p.id === id);
  const equipePessoas = equipe.map(byId).filter(Boolean) as Pessoa[];
  const coordPessoas = coord.map(byId).filter(Boolean) as Pessoa[];

  function toggle(item: ChecklistItem) {
    const cur = isDone(item);
    onProgressoChange({ ...progresso, [etapa]: { ...etapaProg, [item.id]: !cur } });
  }

  function setItemResp(itemId: string, ids: string[]) {
    onResponsaveisChange({ ...responsaveis, [itemId]: ids });
  }

  return (
    <div>
      {/* equipe da etapa (derivada) + coordenador opcional */}
      <div className="border border-border rounded-xl bg-panel-2 px-3.5 py-3 mb-3.5 space-y-2.5">
        <div className="flex items-center gap-3 flex-wrap">
          {equipePessoas.length > 0 ? (
            <FacesStack
              size="md"
              max={6}
              pessoas={equipePessoas.map((p) => ({
                id: p.id, nome: p.nome, avatarUrl: p.avatarUrl, papel: displayStatus(etapa), atual: isAtual,
              }))}
            />
          ) : (
            <span className="w-9 h-9 shrink-0 rounded-full border-2 border-yellow/50 bg-yellow/10 text-yellow flex items-center justify-center font-extrabold">?</span>
          )}
          <div className="min-w-0">
            <div className="text-[10px] text-muted uppercase tracking-widest">Equipe desta etapa · derivada dos itens</div>
            <div className={clsx('text-sm font-bold truncate', equipePessoas.length === 0 && 'text-yellow')}>
              {equipePessoas.length > 0
                ? equipePessoas.map((p) => p.nome.split(' ')[0]).join(', ')
                : '— atribua nos itens abaixo —'}
            </div>
          </div>
        </div>
        {temRespDireto && (
          <div className="flex items-center gap-2 flex-wrap border-t border-border/60 pt-2.5">
            <div className="min-w-0 flex-1">
              <div className="text-[10px] text-muted uppercase tracking-widest">Coordenador da etapa · opcional</div>
              <div className="text-xs font-semibold text-text-2 truncate">
                {coordPessoas.length > 0 ? coordPessoas.map((p) => p.nome.split(' ')[0]).join(', ') : 'quem responde pela etapa (não precisa executar)'}
              </div>
            </div>
            <div className="w-full sm:w-56">
              <PessoasMultiPicker
                pessoas={pessoas}
                selecionados={coord}
                onChange={onCoordChange}
                disabled={disabled}
                placeholder="Definir coordenador…"
              />
            </div>
          </div>
        )}
      </div>

      {/* checklist da fase */}
      {itens.length === 0 ? (
        <div className="bg-panel-2 border border-border rounded-lg p-3 text-[11px] text-muted">
          Sem itens de checklist pra esta etapa neste tipo de produto.
        </div>
      ) : (
        <>
          <div className="text-[10px] text-muted uppercase tracking-widest font-bold mb-2">
            Checklist · {displayStatus(etapa)} · {feitos}/{itens.length}
          </div>
          <div className="space-y-2">
            {itens.map((item) => {
              const checked = isDone(item);
              const resp = responsaveis[item.id] ?? [];
              return (
                <div key={item.id}
                  className={clsx(
                    'rounded-lg border transition-colors',
                    checked ? 'bg-green/5 border-green/30' : 'bg-panel-2 border-border',
                    disabled && 'opacity-60',
                  )}>
                  <label className={clsx(
                    'flex items-start gap-2.5 px-3 py-2 cursor-pointer',
                    disabled && 'cursor-not-allowed',
                  )}>
                    <input type="checkbox" checked={checked} disabled={disabled}
                      onChange={() => !disabled && toggle(item)}
                      className="w-3.5 h-3.5 accent-green mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className={clsx('text-[11.5px] font-semibold', checked ? 'text-text line-through opacity-70' : 'text-text')}>
                        {item.titulo}
                      </div>
                      {item.papeisSugeridos && item.papeisSugeridos.length > 0 && (
                        <div className="text-[9.5px] text-muted mt-0.5">
                          sugerido: {item.papeisSugeridos.join(' / ')}
                        </div>
                      )}
                    </div>
                    {resp.length > 0 && (
                      <FacesStack
                        size="sm"
                        max={3}
                        pessoas={resp
                          .map((id) => pessoas.find((p) => p.id === id))
                          .filter(Boolean)
                          .map((p) => ({ id: p!.id, nome: p!.nome, avatarUrl: p!.avatarUrl }))}
                      />
                    )}
                  </label>
                  {/* Atribuição de responsáveis — multi-select */}
                  <div className="px-3 pb-2 -mt-1">
                    <ResponsaveisPicker
                      pessoas={pessoas}
                      papeisSugeridos={item.papeisSugeridos ?? []}
                      selecionados={resp}
                      onChange={(ids) => setItemResp(item.id, ids)}
                      disabled={disabled}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

/* =========================================================
   INLINE DATE — data que vira <input type=date> ao clicar
   ========================================================= */
function InlineDate({ value, onChange, display, pending, disabled }: {
  value: string;
  onChange: (v: string) => void;
  display: string;
  pending?: boolean;
  disabled?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  if (editing && !disabled) {
    return (
      <input
        autoFocus
        type="date"
        className="input py-1.5 text-sm w-full !border-blue"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => setEditing(false)}
      />
    );
  }
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => setEditing(true)}
      className={clsx(
        'group/id inline-flex items-center gap-1.5 rounded-md border border-transparent px-2 py-1 -ml-2 text-sm font-semibold',
        'hover:border-border hover:bg-panel-3 disabled:cursor-default disabled:hover:bg-transparent disabled:hover:border-transparent',
        pending ? 'text-yellow' : 'text-text',
      )}
    >
      {pending && <span className="w-2 h-2 rounded-full bg-yellow" />}
      <span>{display}</span>
      {!disabled && <Pencil className="w-3 h-3 text-muted opacity-0 group-hover/id:opacity-100 ml-auto" />}
    </button>
  );
}

/* =========================================================
   PESSOAS MULTI PICKER — lista TODAS as pessoas ativas
   (funções mudam sem reescrever código)
   ========================================================= */
function PessoasMultiPicker({ pessoas, selecionados, onChange, disabled, placeholder }: {
  pessoas: Pessoa[];
  selecionados: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  return (
    <ResponsaveisPicker
      pessoas={pessoas}
      papeisSugeridos={[]}
      selecionados={selecionados}
      onChange={onChange}
      disabled={disabled}
      placeholder={placeholder}
    />
  );
}

function Field({ label, required, children }: { label: React.ReactNode; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-text-2 uppercase tracking-wide mb-1">
        {label} {required && <span className="text-red">*</span>}
      </label>
      {children}
    </div>
  );
}

interface Comentario {
  raw: string;
  data?: string;
  autor?: string;
  acao?: 'APROVOU' | 'PEDIU REVISÃO' | 'COMENTOU';
  texto: string;
}

function parseComentarios(raw?: string): Comentario[] {
  if (!raw) return [];
  return raw.split('\n').filter((l) => l.trim()).map((linha) => {
    // formato: [data] Autor AÇÃO: texto  OU  [data] Autor: texto
    const m = linha.match(/^\[([^\]]+)\]\s*(.+?)(?:\s+(APROVOU|PEDIU REVISÃO))?(?::\s*(.*))?$/);
    if (!m) return { raw: linha, texto: linha };
    const [, data, autor, acao, texto] = m;
    return {
      raw: linha,
      data,
      autor: autor?.trim(),
      acao: (acao as Comentario['acao']) ?? (texto ? 'COMENTOU' : undefined),
      texto: (texto ?? '').trim(),
    };
  });
}

function ComentariosSection({ comentarios, onAdd, sending, novo, setNovo, canPost }: {
  comentarios?: string;
  onAdd: (texto: string) => void;
  sending: boolean;
  novo: string;
  setNovo: (s: string) => void;
  canPost: boolean;
}) {
  const items = useMemo(() => parseComentarios(comentarios), [comentarios]);
  return (
    <Field label={`Comentários & feedback${items.length ? ` · ${items.length}` : ''}`}>
      <div className="space-y-2">
        {items.length === 0 ? (
          <div className="bg-panel-2 border border-border rounded p-3 text-[11px] text-muted flex items-center gap-2">
            <MessageSquare className="w-3.5 h-3.5" />
            Sem comentários ainda. Toda aprovação/pedido de revisão do cliente aparece aqui.
          </div>
        ) : (
          <div className="space-y-1.5 max-h-56 overflow-y-auto">
            {items.map((c, i) => {
              const isAprov = c.acao === 'APROVOU';
              const isRev = c.acao === 'PEDIU REVISÃO';
              const accent = isAprov ? 'border-green/30 bg-green/5' : isRev ? 'border-purple/30 bg-purple/5' : 'border-border bg-panel-2';
              const icon = isAprov ? <ThumbsUp className="w-3 h-3 text-green" /> : isRev ? <MessageSquareWarning className="w-3 h-3 text-purple" /> : <MessageSquare className="w-3 h-3 text-blue-light" />;
              return (
                <div key={i} className={`border rounded p-2 ${accent}`}>
                  <div className="flex items-center gap-1.5 mb-1">
                    {icon}
                    <span className="text-[11px] font-semibold text-text">{c.autor ?? '—'}</span>
                    {c.acao && c.acao !== 'COMENTOU' && (
                      <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${isAprov ? 'text-green bg-green/15' : 'text-purple bg-purple/15'}`}>
                        {c.acao}
                      </span>
                    )}
                    {c.data && <span className="text-[10px] text-muted ml-auto">{c.data}</span>}
                  </div>
                  {c.texto && <p className="text-[11px] text-text-2 leading-snug whitespace-pre-wrap">{c.texto}</p>}
                </div>
              );
            })}
          </div>
        )}
        {canPost && (
          <div className="flex items-start gap-2 pt-1">
            <textarea
              rows={2}
              value={novo}
              onChange={(e) => setNovo(e.target.value)}
              placeholder="Adicionar comentário interno (cliente vê no portal)…"
              className="input w-full resize-none text-xs"
            />
            <button
              type="button"
              onClick={() => novo.trim() && onAdd(novo.trim())}
              disabled={sending || !novo.trim()}
              className="btn-primary !px-3 !py-2 disabled:opacity-50"
              title="Enviar"
            >
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            </button>
          </div>
        )}
      </div>
    </Field>
  );
}

function ResponsaveisPicker({
  pessoas, papeisSugeridos, selecionados, onChange, disabled, placeholder,
}: {
  pessoas: Pessoa[];
  papeisSugeridos: string[];
  selecionados: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [busca, setBusca] = useState('');
  // abre para CIMA quando o botão está perto do fundo da tela (últimos itens do checklist)
  const [dropUp, setDropUp] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);

  function toggleOpen() {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setDropUp(window.innerHeight - r.bottom < 340);
    }
    setOpen((v) => !v);
  }

  // Pool sugerido: pessoas cujo papel bate com os papeisSugeridos.
  // (Match por substring case-insensitive — tolerante a variações como "Editor"/"Editor de vídeo")
  const sugeridas = useMemo(() => {
    if (papeisSugeridos.length === 0) return pessoas.filter((p) => p.ativo);
    const lowers = papeisSugeridos.map((s) => s.toLowerCase());
    return pessoas.filter((p) => {
      if (!p.ativo) return false;
      const papel = (p.papel ?? '').toLowerCase();
      return lowers.some((s) => papel.includes(s) || s.includes(papel));
    });
  }, [pessoas, papeisSugeridos]);

  const outras = useMemo(
    () => pessoas.filter((p) => p.ativo && !sugeridas.includes(p)),
    [pessoas, sugeridas],
  );

  const selectedNames = selecionados
    .map((id) => pessoas.find((p) => p.id === id)?.nome)
    .filter(Boolean) as string[];

  function toggle(id: string) {
    if (selecionados.includes(id)) onChange(selecionados.filter((x) => x !== id));
    else onChange([...selecionados, id]);
  }

  const filteredSug = sugeridas.filter((p) => p.nome.toLowerCase().includes(busca.toLowerCase()));
  const filteredOutras = outras.filter((p) => p.nome.toLowerCase().includes(busca.toLowerCase()));

  return (
    <div className="relative">
      <button type="button" disabled={disabled} ref={btnRef}
        onClick={toggleOpen}
        className={clsx(
          'w-full flex items-center gap-1.5 px-2 py-1 rounded text-[10.5px] transition-colors',
          'bg-panel border border-border-light hover:border-blue/40 hover:bg-panel-3',
          disabled && 'cursor-not-allowed opacity-60',
        )}>
        {selectedNames.length === 0 ? (
          <span className="text-muted italic">{placeholder ?? '+ atribuir responsável'}</span>
        ) : (
          <span className="text-text-2 truncate">
            <span className="font-bold text-blue">{selectedNames.length}</span> · {selectedNames.join(', ')}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className={clsx(
            'absolute z-50 left-0 right-0 bg-panel border border-border rounded-md shadow-xl shadow-black/40 max-h-72 overflow-y-auto',
            dropUp ? 'bottom-full mb-1' : 'top-full mt-1',
          )}>
            <div className="p-2 border-b border-border bg-panel-2">
              <input autoFocus
                value={busca} onChange={(e) => setBusca(e.target.value)}
                placeholder="Buscar pessoa..."
                className="input w-full text-xs !py-1" />
            </div>
            {papeisSugeridos.length > 0 && sugeridas.length > 0 && (
              <div className="px-3 py-1 text-[9px] uppercase tracking-wider text-blue font-bold border-b border-border bg-blue/5">
                Sugeridos pra esse passo
              </div>
            )}
            {filteredSug.map((p) => (
              <PickItem key={p.id} pessoa={p}
                checked={selecionados.includes(p.id)}
                onClick={() => toggle(p.id)} />
            ))}
            {papeisSugeridos.length > 0 && outras.length > 0 && (
              <div className="px-3 py-1 text-[9px] uppercase tracking-wider text-muted font-bold border-b border-border bg-panel-2">
                Outras pessoas
              </div>
            )}
            {filteredOutras.map((p) => (
              <PickItem key={p.id} pessoa={p}
                checked={selecionados.includes(p.id)}
                onClick={() => toggle(p.id)} />
            ))}
            {filteredSug.length === 0 && filteredOutras.length === 0 && (
              <div className="px-3 py-4 text-[11px] text-muted text-center">Ninguém encontrado.</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function PickItem({ pessoa, checked, onClick }: { pessoa: Pessoa; checked: boolean; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick}
      className={clsx(
        'w-full flex items-center gap-2 px-3 py-1.5 text-left text-[11px] hover:bg-panel-2 transition-colors',
        checked && 'bg-blue/10',
      )}>
      <input type="checkbox" checked={checked} onChange={() => {}} className="w-3 h-3 accent-blue pointer-events-none" />
      <Avatar nome={pessoa.nome} avatarUrl={pessoa.avatarUrl} size="sm" tooltip={false} />
      <span className="flex-1 truncate">{pessoa.nome}</span>
      {pessoa.papel && <span className="text-[9px] text-muted">{pessoa.papel}</span>}
    </button>
  );
}

/* =========================================================
   TIPO B — Sub-items (lista de clientes dentro do card)
   ========================================================= */
function SubItemsTipoBSection({ subItemsJson, canEdit, onUpdate }: {
  subItemsJson: string;
  canEdit: boolean;
  onUpdate: (items: SubItemTipoB[]) => void;
}) {
  let items: SubItemTipoB[] = [];
  try { items = JSON.parse(subItemsJson); } catch { return null; }

  if (items.length === 0) return null;

  const concluidos = items.filter((i) => i.status === 'concluido').length;
  const statusColor: Record<SubItemTipoB['status'], string> = {
    pendente: 'bg-muted/15 text-muted border-muted/30',
    em_producao: 'bg-orange/15 text-orange border-orange/30',
    concluido: 'bg-green/15 text-green border-green/30',
  };
  const statusLabel: Record<SubItemTipoB['status'], string> = {
    pendente: 'Pendente',
    em_producao: 'Em produção',
    concluido: 'Concluído',
  };
  const nextStatus: Record<SubItemTipoB['status'], SubItemTipoB['status']> = {
    pendente: 'em_producao',
    em_producao: 'concluido',
    concluido: 'pendente',
  };

  function cycleStatus(idx: number) {
    if (!canEdit) return;
    const updated = [...items];
    updated[idx] = { ...updated[idx], status: nextStatus[updated[idx].status] };
    onUpdate(updated);
  }

  return (
    <Field label={<span className="flex items-center gap-1.5">Clientes neste card · {concluidos}/{items.length}</span>}>
      <div className="space-y-1">
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-2 px-2.5 py-1.5 bg-panel-2 border border-border rounded">
            <span className="text-[11px] font-semibold text-text flex-1 truncate uppercase">
              {item.solicitanteNome ?? item.solicitanteId}
            </span>
            <button
              type="button"
              onClick={() => cycleStatus(i)}
              disabled={!canEdit}
              className={clsx(
                'text-[9px] font-bold px-1.5 py-0.5 rounded border transition-colors',
                statusColor[item.status],
                canEdit && 'cursor-pointer hover:opacity-80',
              )}
              title="Clique para mudar status"
            >
              {statusLabel[item.status]}
            </button>
          </div>
        ))}
      </div>
      <div className="mt-1.5">
        <div className="h-1.5 bg-border rounded-full overflow-hidden">
          <div
            className="h-full bg-green rounded-full transition-all"
            style={{ width: `${items.length > 0 ? (concluidos / items.length) * 100 : 0}%` }}
          />
        </div>
      </div>
    </Field>
  );
}

/* =========================================================
   CUSTOM CHECKLIST — itens inter-times
   ========================================================= */
function CustomChecklistSection({ itens, onChange, pessoas, areas, novoTitulo, setNovoTitulo, novoEtapa, setNovoEtapa }: {
  itens: { id: string; titulo: string; etapa: string; responsavelId?: string; prazo?: string; areaId?: string; concluido?: boolean }[];
  onChange: (i: typeof itens) => void;
  pessoas: Pessoa[];
  areas: Area[];
  novoTitulo: string;
  setNovoTitulo: (s: string) => void;
  novoEtapa: string;
  setNovoEtapa: (s: string) => void;
}) {
  function addItem() {
    if (!novoTitulo.trim()) return;
    const id = `custom_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    onChange([...itens, { id, titulo: novoTitulo.trim(), etapa: novoEtapa, concluido: false }]);
    setNovoTitulo('');
  }

  function removeItem(id: string) {
    onChange(itens.filter((i) => i.id !== id));
  }

  function toggleItem(id: string) {
    onChange(itens.map((i) => i.id === id ? { ...i, concluido: !i.concluido } : i));
  }

  function setResp(id: string, respId: string) {
    onChange(itens.map((i) => i.id === id ? { ...i, responsavelId: respId || undefined } : i));
  }

  function setPrazo(id: string, prazo: string) {
    onChange(itens.map((i) => i.id === id ? { ...i, prazo: prazo || undefined } : i));
  }

  function setArea(id: string, areaId: string) {
    onChange(itens.map((i) => i.id === id ? { ...i, areaId: areaId || undefined } : i));
  }

  const ETAPAS = ['Triagem', 'Em produção', 'Em revisão', 'Aguardando aprovação'];
  const ativos = pessoas.filter((p) => p.ativo).sort((a, b) => a.nome.localeCompare(b.nome));

  return (
    <Field label={`Editar checklist de verificação · ${itens.filter(i => i.concluido).length}/${itens.length}`}>
      <div className="space-y-1.5">
        {itens.map((item) => (
          <div key={item.id} className={clsx(
            'border rounded px-2.5 py-2 transition-colors',
            item.concluido ? 'bg-green/5 border-green/30' : 'bg-panel-2 border-border',
          )}>
            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                checked={!!item.concluido}
                onChange={() => toggleItem(item.id)}
                className="w-3.5 h-3.5 accent-green mt-0.5"
              />
              <div className="flex-1 min-w-0">
                <div className={clsx('text-[11px] font-semibold', item.concluido && 'line-through opacity-60')}>
                  {item.titulo}
                </div>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <select
                    className="text-[10px] bg-panel border border-border rounded px-1 py-0.5"
                    value={item.responsavelId ?? ''}
                    onChange={(e) => setResp(item.id, e.target.value)}
                  >
                    <option value="">sem resp.</option>
                    {ativos.map((p) => <option key={p.id} value={p.id}>{p.nome.split(' ')[0]}</option>)}
                  </select>
                  <select
                    className="text-[10px] bg-panel border border-border rounded px-1 py-0.5"
                    value={item.areaId ?? ''}
                    onChange={(e) => setArea(item.id, e.target.value)}
                  >
                    <option value="">sem time</option>
                    {areas.map((a) => <option key={a.id} value={a.id}>{a.nome}</option>)}
                  </select>
                  <input
                    type="date"
                    className="text-[10px] bg-panel border border-border rounded px-1 py-0.5"
                    value={item.prazo ?? ''}
                    onChange={(e) => setPrazo(item.id, e.target.value)}
                  />
                </div>
              </div>
              <button type="button" onClick={() => removeItem(item.id)}
                className="text-muted hover:text-red text-[10px] shrink-0 mt-0.5">✕</button>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-1.5 mt-2">
        <input
          className="input flex-1 text-xs"
          placeholder="Novo item do checklist..."
          value={novoTitulo}
          onChange={(e) => setNovoTitulo(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addItem(); } }}
        />
        <select
          className="input text-xs !w-auto"
          value={novoEtapa}
          onChange={(e) => setNovoEtapa(e.target.value)}
        >
          {ETAPAS.map((et) => <option key={et} value={et}>{et}</option>)}
        </select>
        <button type="button" onClick={addItem}
          className="btn-secondary !px-2 !py-1 text-xs" disabled={!novoTitulo.trim()}>
          + Adicionar
        </button>
      </div>
    </Field>
  );
}
